import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Plus, Star, Trash2, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import AddressSearch from '@/components/AddressSearch';

const empty = {
  label: '', recipient_name: '', phone: '', postal_code: '',
  address1: '', address2: '', memo: '', is_default: false,
  road_address: '', jibun_address: '', base_address: '', detail_address: '', extra_address: '',
};

const inputCls = "px-3 py-2 border border-black/10 dark:border-white/10 rounded-lg text-sm bg-transparent";

const AddressFields = ({ form, setForm }) => {
  const detailRef = useRef(null);
  const applySearchResult = (r) => setForm({
    ...form,
    postal_code: r.postal_code,
    road_address: r.road_address,
    jibun_address: r.jibun_address,
    base_address: r.base_address,
    extra_address: r.extra_address,
    address1: (r.base_address + (r.extra_address ? ' ' + r.extra_address : '')).trim(),
  });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <input placeholder="배송지 이름" className={inputCls} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
      <input placeholder="받는 사람" className={inputCls} value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} />
      <input placeholder="휴대전화" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      <div className="flex gap-2">
        <input placeholder="우편번호" className={`${inputCls} flex-1`} readOnly value={form.postal_code} />
        <AddressSearch onComplete={applySearchResult} detailInputRef={detailRef} testId="addr-search-btn" />
      </div>
      <input placeholder="기본 주소 (주소검색으로 자동 입력)" className={`${inputCls} sm:col-span-2`} readOnly value={form.address1} />
      <input ref={detailRef} placeholder="상세 주소 (직접 입력)" className={`${inputCls} sm:col-span-2`}
        value={form.address2} onChange={(e) => setForm({ ...form, address2: e.target.value, detail_address: e.target.value })} />
      <input placeholder="배송 메모" className={`${inputCls} sm:col-span-2`} value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} />
      <label className="text-xs sm:col-span-2 inline-flex items-center gap-2">
        <input type="checkbox" checked={!!form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} /> 기본 배송지
      </label>
    </div>
  );
};

const MyAddresses = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null); // null | 'new' | address_id
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/buyer/addresses');
      setItems(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) { navigate('/login'); return; }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startEdit = (a) => { setEditId(a.address_id); setForm({ ...empty, ...a }); };
  const startNew = () => { setEditId('new'); setForm(empty); };
  const cancel = () => { setEditId(null); setForm(empty); };

  const submit = async () => {
    if (!form.recipient_name || !form.phone || !form.postal_code || !form.address1) {
      toast.error('필수 항목을 입력해주세요.');
      return;
    }
    setSaving(true);
    try {
      if (editId === 'new') await api.post('/buyer/addresses', form);
      else await api.patch(`/buyer/addresses/${editId}`, form);
      toast.success('저장되었습니다.');
      cancel();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '저장에 실패했습니다.');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('배송지를 삭제하시겠습니까?')) return;
    try { await api.delete(`/buyer/addresses/${id}`); load(); toast.success('삭제되었습니다.'); }
    catch (e) { toast.error(e?.response?.data?.detail || '삭제 실패'); }
  };

  const setDefault = async (id) => {
    try { await api.post(`/buyer/addresses/${id}/default`); load(); toast.success('기본 배송지로 지정되었습니다.'); }
    catch (e) { toast.error(e?.response?.data?.detail || '지정 실패'); }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] pb-16" data-testid="my-addresses-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 sm:pt-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs sm:text-sm text-gray-500 hover:text-black dark:hover:text-white mb-4">
          <ArrowLeft size={14} /> 마이페이지
        </Link>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>배송지 관리</h1>
            <p className="text-sm text-gray-500 mt-1">주문서에서 사용할 배송지를 관리합니다.</p>
          </div>
          <button type="button" onClick={startNew}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-bold"
            data-testid="addresses-add-btn"><Plus size={12} /> 배송지 추가</button>
        </div>

        {loading ? (
          <div className="text-sm text-gray-400">불러오는 중…</div>
        ) : items.length === 0 && editId !== 'new' ? (
          <div className="py-16 text-center bg-black/[0.02] dark:bg-white/[0.03] rounded-2xl border border-dashed border-black/10 dark:border-white/10" data-testid="addresses-empty">
            <MapPin className="mx-auto text-gray-300" size={28} />
            <p className="mt-3 text-sm font-bold">등록된 배송지가 없습니다.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="addresses-list">
            {items.map((a) => (
              <div key={a.address_id} className="rounded-xl border border-black/10 dark:border-white/10 p-3" data-testid={`address-${a.address_id}`}>
                {editId === a.address_id ? (
                  <>
                    <AddressFields form={form} setForm={setForm} />
                    <div className="mt-2 flex gap-2">
                      <button onClick={submit} disabled={saving} className="flex-1 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40" data-testid={`address-save-${a.address_id}`}>저장</button>
                      <button onClick={cancel} className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold">취소</button>
                    </div>
                  </>
                ) : (
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-sm">{a.label || '배송지'}</p>
                        {a.is_default && <span className="text-[10px] font-black text-[#FF3B30] uppercase tracking-widest">기본</span>}
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{a.recipient_name} · {a.phone}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">[{a.postal_code}] {a.address1} {a.address2}</p>
                      {a.memo && <p className="text-[11px] text-gray-500 mt-1">메모: {a.memo}</p>}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      {!a.is_default && <button onClick={() => setDefault(a.address_id)} className="p-1.5 text-[#FF3B30]" title="기본으로 지정" data-testid={`address-default-${a.address_id}`}><Star size={14} /></button>}
                      <button onClick={() => startEdit(a)} className="p-1.5 text-gray-500" title="수정" data-testid={`address-edit-${a.address_id}`}><Edit2 size={14} /></button>
                      <button onClick={() => remove(a.address_id)} className="p-1.5 text-red-500" title="삭제" data-testid={`address-delete-${a.address_id}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {editId === 'new' && (
              <div className="rounded-xl border-2 border-dashed border-[#FF3B30]/40 p-3" data-testid="address-new-form">
                <AddressFields form={form} setForm={setForm} />
                <div className="mt-2 flex gap-2">
                  <button onClick={submit} disabled={saving} className="flex-1 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40" data-testid="address-save-new">저장</button>
                  <button onClick={cancel} className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold">취소</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyAddresses;
